import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    SwapHoriz as SwapIcon,
    Clear as ClearIcon,
    Close as CloseIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import {
    hexToDecimal,
    decimalToHex,
    formatTokenAmount,
    hexToFormattedToken,
    addCommasSeparators,
    formatLargeNumber,
    smartConvert,
    copyToClipboard,
    isValidHex,
    isValidDecimal,
    ConversionResult
} from '../../utils/hexConverter';

interface FloatingConverterProps {
    open: boolean;
    onClose: () => void;
    initialValue?: string;
    initialMode?: 'auto' | 'hex-to-decimal' | 'decimal-to-hex' | 'token';
}

const FloatingConverter: React.FC<FloatingConverterProps> = ({
    open,
    onClose,
    initialValue = '',
    initialMode = 'auto'
}) => {
    const [inputValue, setInputValue] = useState(initialValue);
    const [conversionMode, setConversionMode] = useState<'auto' | 'hex-to-decimal' | 'decimal-to-hex' | 'token'>(initialMode);
    const [tokenDecimals, setTokenDecimals] = useState(18);
    const [tokenSymbol, setTokenSymbol] = useState('');
    const [result, setResult] = useState<ConversionResult | null>(null);
    const [error, setError] = useState<string>('');
    const [copySuccess, setCopySuccess] = useState<string>('');

    // Update input when initialValue changes
    useEffect(() => {
        if (open && initialValue) {
            setInputValue(initialValue);
            setConversionMode(initialMode);
            // Auto-convert if we have an initial value
            setTimeout(() => handleConvert(), 100);
        }
    }, [open, initialValue, initialMode]);

    // Clear state when dialog closes
    useEffect(() => {
        if (!open) {
            setError('');
            setCopySuccess('');
            setResult(null);
        }
    }, [open]);

    // Handle conversion
    const handleConvert = () => {
        if (!inputValue.trim()) {
            setError('Please enter a value to convert');
            setResult(null);
            return;
        }

        setError('');
        setCopySuccess('');

        try {
            let conversionResult: ConversionResult;

            switch (conversionMode) {
                case 'auto':
                    conversionResult = smartConvert(inputValue);
                    break;

                case 'hex-to-decimal':
                    if (!isValidHex(inputValue)) {
                        throw new Error('Invalid hex format');
                    }
                    const decimal = hexToDecimal(inputValue);
                    conversionResult = {
                        original: inputValue,
                        decimal,
                        formatted: addCommasSeparators(decimal),
                        type: 'hex',
                        confidence: 1,
                        suggestions: ['Manual hex to decimal conversion']
                    };
                    break;

                case 'decimal-to-hex':
                    if (!isValidDecimal(inputValue)) {
                        throw new Error('Invalid decimal format');
                    }
                    const hex = decimalToHex(inputValue);
                    conversionResult = {
                        original: inputValue,
                        decimal: inputValue,
                        formatted: hex,
                        type: 'hex',
                        confidence: 1,
                        suggestions: ['Manual decimal to hex conversion']
                    };
                    break;

                case 'token':
                    if (!isValidHex(inputValue)) {
                        throw new Error('Invalid hex format for token conversion');
                    }
                    const tokenFormatted = hexToFormattedToken(inputValue, tokenDecimals, tokenSymbol || undefined);
                    const tokenDecimal = hexToDecimal(inputValue);
                    conversionResult = {
                        original: inputValue,
                        decimal: tokenDecimal,
                        formatted: tokenFormatted,
                        type: 'amount',
                        confidence: 1,
                        suggestions: [`Formatted as ${tokenDecimals}-decimal token`]
                    };
                    break;

                default:
                    throw new Error('Unknown conversion mode');
            }

            setResult(conversionResult);

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Conversion failed';
            setError(errorMessage);
            setResult(null);
        }
    };

    // Handle copy to clipboard
    const handleCopy = async (text: string, label: string) => {
        const success = await copyToClipboard(text);
        if (success) {
            setCopySuccess(`${label} copied!`);
            setTimeout(() => setCopySuccess(''), 2000);
        } else {
            setError('Failed to copy to clipboard');
        }
    };

    // Clear all fields
    const handleClear = () => {
        setInputValue('');
        setResult(null);
        setError('');
        setCopySuccess('');
    };

    // Handle Enter key
    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleConvert();
        }
    };

    // Handle Escape key to close
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            onKeyDown={handleKeyDown}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Quick Hex/Decimal Converter
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Input Section */}
                <TextField
                    fullWidth
                    label="Enter hex or decimal value"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="0x1e18 or 7704"
                    sx={{ mb: 2 }}
                    multiline
                    rows={2}
                    autoFocus
                />

                {/* Conversion Mode */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Mode</InputLabel>
                        <Select
                            value={conversionMode}
                            onChange={(e) => setConversionMode(e.target.value as any)}
                            label="Mode"
                            size="small"
                        >
                            <MenuItem value="auto">Auto Detect</MenuItem>
                            <MenuItem value="hex-to-decimal">Hex → Decimal</MenuItem>
                            <MenuItem value="decimal-to-hex">Decimal → Hex</MenuItem>
                            <MenuItem value="token">Token Format</MenuItem>
                        </Select>
                    </FormControl>

                    {conversionMode === 'token' && (
                        <>
                            <TextField
                                label="Decimals"
                                type="number"
                                value={tokenDecimals}
                                onChange={(e) => setTokenDecimals(parseInt(e.target.value) || 18)}
                                sx={{ width: 100 }}
                                size="small"
                                inputProps={{ min: 0, max: 30 }}
                            />
                            <TextField
                                label="Symbol"
                                value={tokenSymbol}
                                onChange={(e) => setTokenSymbol(e.target.value)}
                                sx={{ width: 120 }}
                                size="small"
                                placeholder="ETH"
                            />
                        </>
                    )}
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        onClick={handleConvert}
                        startIcon={<SwapIcon />}
                        size="small"
                    >
                        Convert
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        startIcon={<ClearIcon />}
                        size="small"
                    >
                        Clear
                    </Button>
                </Box>

                {/* Error/Success Messages */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {copySuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {copySuccess}
                    </Alert>
                )}

                {/* Results */}
                {result && (
                    <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />

                        {/* Type Detection */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Chip
                                label={result.type.toUpperCase()}
                                color={result.confidence > 0.7 ? 'success' : result.confidence > 0.4 ? 'warning' : 'default'}
                                size="small"
                            />
                            <Typography variant="body2" color="text.secondary">
                                {Math.round(result.confidence * 100)}% confidence
                            </Typography>
                        </Box>

                        {/* Results Grid */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Original */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Original:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                                        {result.original}
                                    </Typography>
                                    <Tooltip title="Copy">
                                        <IconButton size="small" onClick={() => handleCopy(result.original, 'Original')}>
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>

                            {/* Decimal */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Decimal:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                                        {result.decimal}
                                    </Typography>
                                    <Tooltip title="Copy">
                                        <IconButton size="small" onClick={() => handleCopy(result.decimal, 'Decimal')}>
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>

                            {/* Formatted */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Formatted:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                                        {result.formatted}
                                    </Typography>
                                    <Tooltip title="Copy">
                                        <IconButton size="small" onClick={() => handleCopy(result.formatted, 'Formatted')}>
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>

                            {/* Additional formats for amounts */}
                            {result.type === 'amount' && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Large Format:
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>
                                            {formatLargeNumber(result.decimal)}
                                        </Typography>
                                        <Tooltip title="Copy">
                                            <IconButton size="small" onClick={() => handleCopy(formatLargeNumber(result.decimal), 'Large format')}>
                                                <CopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Suggestions */}
                        {result.suggestions.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <InfoIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Suggestions:
                                </Typography>
                                <List dense>
                                    {result.suggestions.slice(0, 3).map((suggestion, index) => (
                                        <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                                            <ListItemText
                                                primary={suggestion}
                                                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    Press Enter to convert, Escape to close
                </Typography>
                <Button onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FloatingConverter;
